<mj-page-layout>
    <mj-page-body>
        <!-- The mockup's `.top`: a bare 27px/800 h1 over one sub line, no icon chip (settings.html:311). -->
        <agu-plain-header Title="Settings" Sub="What the hub knows about you, and where each part is managed" />

        <!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), then the error panel. -->
        @if (loading) {
            <agu-skeleton [Count]="3" />
        } @else if (error) {
            <agu-state-panel
                Variant="error"
                [Icon]="ErrorCopy.Icon"
                [Title]="ErrorCopy.Title"
                [Body]="ErrorCopy.Body"
                PrimaryLabel="Try again"
                [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
                [SecondaryHref]="failures.SupportHref"
                [Foot]="failures.Foot"
                [Note]="failures.Note"
                (Primary)="Retry()"
            />
        } @else if (profile && config) {
            <!--
              Three destinations own different pieces of the member's identity.
              Naming them up front, and marking the two that leave, stops the
              hub from looking like it can edit records it does not hold
              (settings.html:315-332).
            -->
            <div class="ra-settings__card">
                <h2 class="ra-settings__card-title">Where things are managed</h2>
                <!-- Tile glyphs are the mockup's own 15px outline svgs (settings.html:326,331,336). -->
                <div class="ra-settings__dest">
                    <button type="button" class="ra-settings__tile" (click)="OnViewMyProfile()">
                        <span class="ra-settings__tile-top">
                            <span class="ra-settings__tile-icon ra-settings__tile-icon--blue" aria-hidden="true"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="2.6" /></svg></span>
                            <b>Member hub profile</b>
                        </span>
                        <span class="ra-settings__tile-sub">How you appear to other members in People Finder.</span>
                        <span class="ra-settings__tile-go">View my hub profile <span aria-hidden="true">→</span></span>
                    </button>

                    @if (config.publicProfileUrl) {
                        <a class="ra-settings__tile" [href]="config.publicProfileUrl" target="_blank" rel="noopener">
                            <span class="ra-settings__tile-top">
                                <span class="ra-settings__tile-icon ra-settings__tile-icon--teal" aria-hidden="true"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-5 9 5-9 5z" /><path d="M7 11.5V17c0 1.1 2.2 2 5 2s5-.9 5-2v-5.5" /></svg></span>
                                <b>AGU public profile</b>
                            </span>
                            <span class="ra-settings__tile-sub">Your research record: name, institution, career stage, ORCID and disciplines.</span>
                            <span class="ra-settings__tile-go">View public profile <span aria-hidden="true">↗</span></span>
                        </a>
                    } @else {
                        <div class="ra-settings__tile ra-settings__tile--disabled" aria-disabled="true">
                            <span class="ra-settings__tile-top">
                                <span class="ra-settings__tile-icon ra-settings__tile-icon--teal" aria-hidden="true"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-5 9 5-9 5z" /><path d="M7 11.5V17c0 1.1 2.2 2 5 2s5-.9 5-2v-5.5" /></svg></span>
                                <b>AGU public profile</b>
                            </span>
                            <span class="ra-settings__tile-sub">Not linked to an AGU member record</span>
                        </div>
                    }

                    <a class="ra-settings__tile" [href]="config.accountSettingsUrl" target="_blank" rel="noopener">
                        <span class="ra-settings__tile-top">
                            <span class="ra-settings__tile-icon ra-settings__tile-icon--orange" aria-hidden="true"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg></span>
                            <b>Account settings</b>
                        </span>
                        <span class="ra-settings__tile-sub">Sign-in email, password, membership and billing.</span>
                        <span class="ra-settings__tile-go">Manage account <span aria-hidden="true">↗</span></span>
                    </a>
                </div>
            </div>

            <div class="ra-settings__card">
                <div class="ra-settings__card-head">
                    <h2 class="ra-settings__card-title">Research profile</h2>
                    <a class="ra-settings__edit" [href]="config.accountSettingsUrl" target="_blank" rel="noopener">
                        Edit in account settings <span aria-hidden="true">↗</span>
                    </a>
                </div>
                <dl class="ra-settings__rows">
                    <div class="ra-settings__row">
                        <dt>Name</dt>
                        <dd>{{ profile.name }}</dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>Primary field</dt>
                        <dd>{{ PrimaryField }}</dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>Career stage</dt>
                        <dd>{{ CareerStage }}</dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>Institution</dt>
                        <dd>{{ Institution }}</dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>ORCID iD</dt>
                        <dd>
                            @if (profile.orcid) {
                                <a [href]="'https://orcid.org/' + profile.orcid" target="_blank" rel="noopener">{{ profile.orcid }} <span aria-hidden="true">↗</span></a>
                            } @else {
                                —
                            }
                        </dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>Research areas <small>Used to tailor your recommendations</small></dt>
                        <dd>
                            @if (profile.researchAreas.length > 0) {
                                <ul class="ra-settings__areas">
                                    @for (area of profile.researchAreas; track area) {
                                        <li class="ra-settings__area">{{ area }}</li>
                                    }
                                </ul>
                            } @else {
                                —
                            }
                        </dd>
                    </div>
                </dl>
            </div>

            <div class="ra-settings__card ra-settings__card--last">
                <div class="ra-settings__card-head">
                    <h2 class="ra-settings__card-title">Account</h2>
                    <a class="ra-settings__edit" [href]="config.accountSettingsUrl" target="_blank" rel="noopener">
                        Manage on agu.org <span aria-hidden="true">↗</span>
                    </a>
                </div>
                <dl class="ra-settings__rows">
                    <div class="ra-settings__row">
                        <dt>Email</dt>
                        <dd>{{ Email }}</dd>
                    </div>
                    <div class="ra-settings__row">
                        <dt>AGU membership</dt>
                        <dd>{{ MembershipLine }}</dd>
                    </div>
                </dl>
            </div>
        }
    </mj-page-body>
</mj-page-layout>
